import React, { useState } from 'react';
import { certApi } from '../../api/certApi';

export default function SingleCertView({ activeUser, inspectedHash, onClearInspection }) {
  const [studentName, setStudentName] = useState('');
  const [certId, setCertId] = useState('');
  const [docType, setDocType] = useState('SSLC');
  const [file, setFile] = useState(null);

  const [statusMsg, setStatusMsg] = useState({ text: '', isError: false });
  const [result, setResult] = useState(null);
  const [latencyData, setLatencyData] = useState(null);
  const [historyTrail, setHistoryTrail] = useState(null);

  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);

  const showStatus = (text, isError = false) => setStatusMsg({ text, isError });

  const createFormData = () => {
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('studentName', studentName.trim());
    formData.append('certId', certId.trim());
    formData.append('docType', docType.trim());
    return formData;
  };

// Auto-verify when a hash is passed from Bulk View
  React.useEffect(() => {
    if (!inspectedHash) return;

    const runInspection = async () => {
      setLoading(true);
      setResult(null);
      setLatencyData(null);
      setHistoryTrail(null);
      showStatus(`Querying Fabric Ledger for Hash: ${inspectedHash.substring(0, 16)}...`, false);

      try {
        const res = await certApi.verifyByHash(inspectedHash, activeUser);
        setResult(res.data);
        setLatencyData(res.data.latency || null);
        showStatus(
          res.data.isRevoked
            ? 'Record located on ledger, but it is marked REVOKED.'
            : 'Verified: Record is anchored and active on the Fabric ledger!',
          res.data.isRevoked
        );
      } catch (err) {
        showStatus(err.response?.data?.error || 'Failed to resolve hash from ledger.', true);
      } finally {
        setLoading(false);
        onClearInspection();
      }
    };

    runInspection();
  }, [inspectedHash, activeUser, onClearInspection]); // <-- Added onClearInspection

  const handleIssue = async () => {
    if (!file || !studentName.trim() || !certId.trim()) {
      showStatus('Please provide a file, student name, and certificate ID.', true);
      return;
    }
    setLoading(true);
    setResult(null);
    setLatencyData(null);
    setHistoryTrail(null);

    try {
      const res = await certApi.issueSingle(createFormData(), activeUser);
      showStatus(`Certificate registered! Textual Hash: ${res.data.certHash.substring(0, 16)}...`, false);
      setLatencyData(res.data.latency);
      setResult({
        certHash: res.data.certHash,
        certificate: {
          certId: res.data.certId,
          docType: docType.trim(),
          issuer: `Pareeksha Bhavan (${activeUser})`,
          issuedAt: new Date().toISOString(),
          isRevoked: false
        }
      });
    } catch (err) {
      showStatus(err.response?.data?.error || err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!file || !studentName.trim() || !certId.trim()) {
      showStatus('Please provide a file, student name, and certificate ID.', true);
      return;
    }
    setLoading(true);
    setResult(null);
    setLatencyData(null);
    setHistoryTrail(null);

    try {
      const res = await certApi.verifySingle(createFormData(), activeUser);
      setResult(res.data);
      setLatencyData(res.data.latency);
      showStatus(
        res.data.isRevoked
          ? 'Certificate record found, but it has been REVOKED.'
          : 'Document verified: Content and visuals matched on Fabric ledger!',
        res.data.isRevoked
      );
    } catch (err) {
      showStatus(err.response?.data?.error || 'Verification failed: Content altered or record not found.', true);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchHistory = async () => {
    if (!result?.certHash) return;
    setHistoryLoading(true);
    try {
      const res = await certApi.getHistory(result.certHash, activeUser);
      setHistoryTrail(res.data.history || []);
      showStatus(`Retrieved ${res.data.auditTrailLength || res.data.history?.length || 0} lifecycle records.`, false);
    } catch (err) {
      showStatus(err.response?.data?.error || 'Failed to retrieve history.', true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!result?.certHash) return;
    const reason = window.prompt('Enter reason for revoking this certificate:', 'Document invalidated by authority');
    if (!reason) return;

    setRevokeLoading(true);
    try {
      const res = await certApi.revokeCert(result.certHash, reason, activeUser);
      showStatus(`Revocation successful: ${res.data.message}`, true);
      setResult(prev => ({
        ...prev,
        isRevoked: true,
        certificate: { ...prev?.certificate, isRevoked: true, revokeReason: reason }
      }));
      if (historyTrail) handleFetchHistory();
    } catch (err) {
      showStatus(err.response?.data?.error || 'Failed to revoke certificate.', true);
    } finally {
      setRevokeLoading(false);
    }
  };

  return (
    <div>
      {statusMsg.text && (
        <div style={{
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '1.5rem',
          backgroundColor: statusMsg.isError ? '#f8d7da' : '#d4edda',
          color: statusMsg.isError ? '#721c24' : '#155724',
          border: `1px solid ${statusMsg.isError ? '#f5c6cb' : '#c3e6cb'}`
        }}>
          {statusMsg.text}
        </div>
      )}

      {/* Main Input Form */}
      <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <form style={{ display: 'grid', gap: '14px' }}>
          <div>
            <label><strong>Upload Certificate PDF:</strong></label>
            <input
              type="file"
              accept="application/pdf"
              onChange={e => setFile(e.target.files[0])}
              style={{ display: 'block', marginTop: '6px' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label><strong>Student Name:</strong></label>
              <input
                type="text"
                placeholder="e.g. AKARSH PRAVEEN"
                value={studentName}
                onChange={e => setStudentName(e.target.value)}
                style={{ width: '100%', padding: '8px', marginTop: '4px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label><strong>Certificate / Register ID:</strong></label>
              <input
                type="text"
                placeholder="e.g. REG-2026-8890"
                value={certId}
                onChange={e => setCertId(e.target.value)}
                style={{ width: '100%', padding: '8px', marginTop: '4px', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div>
            <label><strong>Document / Exam Type:</strong></label>
            <input
              type="text"
              value={docType}
              onChange={e => setDocType(e.target.value)}
              style={{ width: '100%', padding: '8px', marginTop: '4px', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={handleIssue}
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#0056b3',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Processing...' : '1. Issue (Anchor Content Hash)'}
            </button>

            <button
              type="button"
              onClick={handleVerify}
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Processing...' : '2. Verify Against Ledger'}
            </button>
          </div>
        </form>
      </div>

      {/* Latency Breakdown */}
      {latencyData && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          border: '1px solid #e9ecef',
          fontSize: '0.9rem'
        }}>
          <strong>Execution Latency Breakdown:</strong>
          <div style={{ display: 'flex', gap: '15px', marginTop: '6px', flexWrap: 'wrap' }}>
            <span>Extraction: <strong>{latencyData.extractionMs?.toFixed(2)} ms</strong></span>
            <span>Canonicalization: <strong>{latencyData.canonicalizationMs?.toFixed(2)} ms</strong></span>
            <span>Ledger: <strong>{(latencyData.blockchainCommitMs || latencyData.gatewayRpcMs)?.toFixed(2)} ms</strong></span>
            <span>Total: <strong>{latencyData.totalMs?.toFixed(2)} ms</strong></span>
          </div>
        </div>
      )}

      {/* Verification Result Card */}
      {result && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1.2rem',
          borderRadius: '6px',
          backgroundColor: (result.isRevoked || result.certificate?.isRevoked) ? '#fff3cd' : '#e8f4f8',
          borderLeft: `6px solid ${(result.isRevoked || result.certificate?.isRevoked) ? '#dc3545' : '#28a745'}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, color: (result.isRevoked || result.certificate?.isRevoked) ? '#721c24' : '#28a745' }}>
              {(result.isRevoked || result.certificate?.isRevoked) ? '⚠️ RECORD LOCATED (REVOKED)' : '✅ GENUINE & UNTAMPERED DOCUMENT'}
            </h3>

            {!(result.isRevoked || result.certificate?.isRevoked) && (
              <button
                type="button"
                onClick={handleRevoke}
                disabled={revokeLoading}
                style={{
                  padding: '6px 14px',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: revokeLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '0.85rem'
                }}
              >
                {revokeLoading ? 'Submitting...' : '🚫 Revoke Certificate'}
              </button>
            )}
          </div>

          <div style={{ marginTop: '10px' }}>
            <p><strong>SHA-256 Textual Hash:</strong> <code style={{ wordBreak: 'break-all' }}>{result.certHash}</code></p>
            <p><strong>Certificate ID:</strong> {result.certificate?.certId || certId}</p>
            <p><strong>Document Type:</strong> {result.certificate?.docType || docType}</p>
            <p><strong>Issuing Authority:</strong> {result.certificate?.issuer || 'Pareeksha Bhavan'}</p>
            {(result.isRevoked || result.certificate?.isRevoked) && (
              <p style={{ color: '#dc3545' }}>
                <strong>Revocation Reason:</strong> {result.certificate?.revokeReason || 'Revoked by authority'}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={handleFetchHistory}
            disabled={historyLoading}
            style={{
              marginTop: '1rem',
              padding: '10px 18px',
              backgroundColor: '#17a2b8',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: historyLoading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold'
            }}
          >
            {historyLoading ? 'Querying Blocks...' : '📜 View Immutable Audit Trail Timeline'}
          </button>

          {/* Audit Trail Timeline */}
          {historyTrail && (
            <div style={{ marginTop: '1.5rem', borderTop: '1px solid #ced4da', paddingTop: '1rem' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#114b5f' }}>
                Immutable Ledger Provenance ({historyTrail.length} Event{historyTrail.length !== 1 ? 's' : ''})
              </h4>
              <div style={{ position: 'relative', paddingLeft: '24px', borderLeft: '3px solid #17a2b8' }}>
                {historyTrail.map((entry, idx) => {
                  const isRevokeEvent = entry.record?.isRevoked || entry.isDelete;
                  return (
                    <div key={entry.txId || idx} style={{ marginBottom: '1.5rem', position: 'relative' }}>
                      <div style={{
                        position: 'absolute',
                        left: '-31px',
                        top: '0px',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: isRevokeEvent ? '#dc3545' : '#17a2b8',
                        border: '2px solid #fff'
                      }} />
                      <div style={{ background: '#fff', padding: '10px 14px', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 'bold', color: isRevokeEvent ? '#dc3545' : '#28a745' }}>
                            {isRevokeEvent ? 'REVOCATION COMMIT' : (idx === 0 ? 'ORIGIN COMMIT (ISSUANCE)' : 'STATE UPDATE')}
                          </span>
                          <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>{entry.timestamp}</span>
                        </div>
                        <div style={{ fontSize: '0.82rem', wordBreak: 'break-all', color: '#495057' }}>
                          <strong>Transaction ID:</strong> <code>{entry.txId}</code>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}